import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {
  FETCH_PAYMENT_TIMEOUT_MINUTES,
  PaymentMethodDataProps,
} from "_interfaces/_common/payment";
import { RechargeAirtimeProps } from "_interfaces/http-requests/recharge";
import { RechargeAirtimeModel } from "_models/data/recharge/data.rechargeAirtime.model";
import { WalletPaymentModel } from "_models/data/wallet/data.walletPayment.model";
import { RechargeAirtimeErrorModel } from "_models/errors/recharge/error.rechargeAirtime.model";
import InputText from "component/_common/forms/inputText";
import PaymentCheckout from "component/_common/payment/paymentCheckout";
import { paymentOptionsList } from "component/_common/payment/paymentOptionsList";
import WalletPaymentMethod from "component/_common/payment/walletPaymentMethod";
import useProcessStatusModal from "component/_common/status/useProcessStatusModal";
import { AppStatusCode } from "config/appStatusCode";
import { formatPriceWithDelimiter } from "functions/helper";
import { HTTP_ERROR } from "functions/http";
import {
  RechargeAirtime,
  RechargeFulfill,
} from "functions/http-requests/recharge";
import { isValidAmount } from "functions/validators";
import RouteConstant from "navigation/constant";
import React, {
  ChangeEvent,
  FormEvent,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAlert } from "state/slice/alert";
import { toggleLoading } from "state/slice/loading";
import { RootState } from "state/store";
import { validateAirtimeRecharge } from "../my-wallet/formValidator";

interface AirtimeRechargeProps {
  handleRefresh?: () => void;
  handleClose: () => void;
}

const AirtimeRecharge: React.FC<AirtimeRechargeProps> = ({
  handleRefresh,
  handleClose,
}) => {
  const Dispatch = useDispatch();
  const navigate = useNavigate();

  const { userInfo } = useSelector((state: RootState) => state.user);

  const [activeRechargeId, setActiveRechargeId] = useState<string>("");
  const [processingSnapshot, setProcessingSnapshot] = useState<{
    phoneNumber?: string;
    amount?: number;
    paymentBy?: string;
  }>({});
  const [processFailedMessage, setProcessFailedMessage] = useState<string>("");
  const DEFAULT_AIRTIME_FAILED_MESSAGE =
    "We could not confirm this balance top-up right now. Please wait a few minutes and try again. If you were charged, the balance may still update automatically.";

  const RECHARGE_POLL_SECONDS = 10;
  const RECHARGE_POLL_TIMEOUT_SECONDS = FETCH_PAYMENT_TIMEOUT_MINUTES * 60;

  const airtimeProcessModal = useProcessStatusModal({
    timeoutSeconds: RECHARGE_POLL_TIMEOUT_SECONDS,
    processingGuardMessage:
      "Your airtime balance is being updated. Do not refresh, close, or go back.",
  });
  const {
    open: processModalOpen,
    status: processModalStatus,
    countdownLabel: processCountdownLabel,
    startProcessing: startProcessModal,
    setFailed: setProcessFailed,
    setDelayed: setProcessDelayed,
    close: closeProcessModal,
  } = airtimeProcessModal;

  const [state, setState] = useState<RechargeAirtimeModel>(
    new RechargeAirtimeModel(),
  );
  const [errors, setErrors] = useState<RechargeAirtimeErrorModel>(
    new RechargeAirtimeErrorModel(),
  );

  const [openPaymentOption, setOpenPaymentOption] = useState<boolean>(false);
  const [openWalletPaymentMethod, setOpenWalletPaymentMethod] =
    useState<boolean>(false);

  const [walletPaymentState, setWalletPaymentState] =
    useState<WalletPaymentModel>(new WalletPaymentModel());

  const beginCheckoutProcessUi = useCallback(() => {
    setOpenWalletPaymentMethod(false);
    startProcessModal();
  }, [startProcessModal]);

  const resolveFailedMessage = useCallback(
    (message?: string) => message || DEFAULT_AIRTIME_FAILED_MESSAGE,
    [],
  );

  const airtimeDetails = useMemo(() => {
    return [
      {
        label: "Phone Number",
        value:
          processingSnapshot.phoneNumber ||
          state?.phoneNumber ||
          userInfo?.phone ||
          "-",
      },
      {
        label: "Amount",
        value:
          typeof processingSnapshot.amount === "number"
            ? `UGX ${formatPriceWithDelimiter(processingSnapshot.amount)}`
            : "-",
      },
    ];
  }, [processingSnapshot, state?.phoneNumber, userInfo?.phone]);

  const handleTogglePaymentOption = () =>
    setOpenPaymentOption(!openPaymentOption);

  const handleToggleWalletPaymentMethod = () =>
    setOpenWalletPaymentMethod(!openWalletPaymentMethod);

  const handleFocus = (name: string) => {
    if (name) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target as HTMLInputElement;
    if (errors?.[name as keyof RechargeAirtimeErrorModel]) {
      setErrors((prev) => ({ ...prev, amount: "" }));
      return;
    }
    if (value && name === "amount" && !isValidAmount(value)) {
      setErrors((prev) => ({ ...prev, amount: "Invalid amount" }));
      return;
    }
    setState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  let handleRechargeRequest = (DATA: PaymentMethodDataProps) => {
    if (
      DATA?.paymentBy &&
      DATA?.paymentMethod &&
      state?.phoneNumber &&
      state?.amount
    ) {
      let PAYLOAD_DATA: RechargeAirtimeProps["DATA"] = {
        phoneNumber: state.phoneNumber,
        amount: Number(state.amount),
        paymentMethod: DATA.paymentMethod,
        paymentBy: DATA.paymentBy,
        momoPhoneNumber: DATA.momoPhoneNumber || undefined,
        airtelMOMOPhone: DATA.airtelMOMOPhone || undefined,
      };

      setProcessingSnapshot({
        phoneNumber: state.phoneNumber,
        amount: Number(state.amount || 0),
        paymentBy: DATA.paymentBy,
      });

      if (DATA.paymentBy !== "wallet") {
        setProcessFailedMessage("");
        beginCheckoutProcessUi();
        setActiveRechargeId("");
      } else {
        Dispatch(toggleLoading(true));
      }

      RechargeAirtime({ DATA: PAYLOAD_DATA })
        .then((res) => {
          const data = res?.data;
          const rechargeId = data?.data?.rechargeId;

          if (data?.statusCode === AppStatusCode.api_success && rechargeId) {
            if (DATA.paymentBy === "wallet") {
              const walletPaymentId = data?.data?.walletRequestId;
              setWalletPaymentState((prev) => ({
                ...prev,
                walletPaymentId: walletPaymentId,
                rechargeId: rechargeId,
              }));
              handleToggleWalletPaymentMethod();
              Dispatch(toggleLoading(false));
            } else {
              setActiveRechargeId(rechargeId);
            }
          } else {
            if (DATA.paymentBy !== "wallet") {
              setProcessFailedMessage(resolveFailedMessage(data?.message));
              setProcessFailed();
            } else {
              Dispatch(
                setAlert({
                  type: data?.level,
                  message: data?.message || "Something went wrong!",
                }),
              );
              Dispatch(toggleLoading(false));
            }
          }
        })
        .catch((error) => {
          Dispatch(setAlert({ type: "error", message: HTTP_ERROR(error) }));
          if (DATA.paymentBy !== "wallet") {
            setProcessFailedMessage(resolveFailedMessage(HTTP_ERROR(error)));
            setProcessFailed();
          }
          Dispatch(toggleLoading(false));
        });
    }
  };

  const handleCloseAllAirtimeModals = useCallback(() => {
    closeProcessModal();
    setActiveRechargeId("");
    Dispatch(toggleLoading(false));
    setOpenPaymentOption(false);
    setOpenWalletPaymentMethod(false);
    setWalletPaymentState(new WalletPaymentModel());
    handleClose();
    setState(new RechargeAirtimeModel());
  }, [handleClose, closeProcessModal, Dispatch]);

  const handleCancelProcess = useCallback(() => {
    if (handleRefresh) handleRefresh();
    handleCloseAllAirtimeModals();
  }, [handleRefresh, handleCloseAllAirtimeModals]);

  const airtimeEmbeddedPaymentProcess = useMemo(() => {
    if (!processModalOpen) return null;
    const paidWithWallet = processingSnapshot.paymentBy === "wallet";
    return {
      status: processModalStatus,
      processingTitle: "Adding airtime balance",
      successTitle: "Balance updated",
      failedTitle: "Could not add balance",
      delayedTitle: "Balance update pending",
      processingMessage: paidWithWallet
        ? "Please do not press back, close this window, or refresh the page while we confirm your wallet payment and add airtime to this number."
        : "Please do not press back, close this window, or refresh the page while we confirm your payment and add airtime to this number.",
      successMessage:
        "The airtime has been added to the balance for this number.",
      failedMessage: processFailedMessage || DEFAULT_AIRTIME_FAILED_MESSAGE,
      delayedMessage:
        "This balance top-up is still being processed. Please wait up to 30 minutes; your airtime balance should update once the payment gateway confirms. If it still has not updated after 30 minutes, please contact support.",
      processingWarningMessage: paidWithWallet
        ? "Please do not press back, close this window, or refresh the page while we confirm your wallet payment and update your airtime balance."
        : "Please do not press back, close this window, or refresh the page while we confirm your payment and update your airtime balance.",
      details: airtimeDetails,
      onPrimaryAction:
        processModalStatus === "failed" || processModalStatus === "delayed"
          ? () => {
              handleCloseAllAirtimeModals();
              navigate(RouteConstant.CONTACT_US);
            }
          : undefined,
      primaryActionLabel: "Contact Support",
      onClose: handleCloseAllAirtimeModals,
    };
  }, [
    processModalOpen,
    processModalStatus,
    processFailedMessage,
    processingSnapshot.paymentBy,
    processCountdownLabel,
    airtimeDetails,
    DEFAULT_AIRTIME_FAILED_MESSAGE,
    handleCloseAllAirtimeModals,
    navigate,
  ]);

  const handleRechargeRequestUpdate = (ID: string) => {
    Dispatch(toggleLoading(false));
    setActiveRechargeId(ID);
    beginCheckoutProcessUi();
  };

  useEffect(() => {
    if (
      !processModalOpen ||
      processModalStatus !== "processing" ||
      !activeRechargeId
    )
      return;

    let intervalId: NodeJS.Timeout;
    let timeoutId: NodeJS.Timeout;
    let stopped = false;

    const stop = () => {
      stopped = true;
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };

    const poll = () => {
      if (stopped) return;

      RechargeFulfill(activeRechargeId)
        .then((res) => {
          const data = res?.data;

          if (data?.statusCode === AppStatusCode.api_success) {
            const STATUS = data?.data?.status?.toLowerCase();
            if (STATUS === "success") {
              stop();
              Dispatch(
                setAlert({
                  type: data?.level,
                  message: data?.message,
                }),
              );
              handleCancelProcess();
            } else if (STATUS === "pending") {
              // keep polling
            } else {
              setProcessFailedMessage(resolveFailedMessage(data?.message));
              setProcessFailed();
              stop();
            }
          } else {
            setProcessFailedMessage(resolveFailedMessage(data?.message));
            setProcessFailed();
            stop();
          }
        })
        .catch((error) => {
          setProcessFailedMessage(resolveFailedMessage(HTTP_ERROR(error)));
          setProcessFailed();
          stop();
        });
    };

    poll();
    intervalId = setInterval(poll, RECHARGE_POLL_SECONDS * 1000);
    timeoutId = setTimeout(() => {
      stop();
      setProcessDelayed();
    }, RECHARGE_POLL_TIMEOUT_SECONDS * 1000);

    return () => stop();
  }, [
    processModalOpen,
    processModalStatus,
    activeRechargeId,
    Dispatch,
    RECHARGE_POLL_TIMEOUT_SECONDS,
    setProcessFailed,
    setProcessDelayed,
    resolveFailedMessage,
    handleCancelProcess,
  ]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    let ValidateStep: {
      valid: boolean;
      errors: { name: string; error: string }[];
    } = validateAirtimeRecharge(state);

    if (!ValidateStep?.valid) {
      for (
        let i = 0, item: { name: string; error: string };
        !!(item = ValidateStep.errors[i++]);
      ) {
        setErrors((prevState) => ({ ...prevState, [item.name]: item.error }));
      }
      return;
    }

    handleTogglePaymentOption();
  };

  const inputList = [
    {
      type: "number",
      name: "phoneNumber",
      label: "Enter phone number",
    },
    {
      type: "number",
      name: "amount",
      label: "Enter amount",
    },
  ];

  useEffect(() => {
    if (userInfo?.phone) {
      setState((prev) => ({
        ...prev,
        phoneNumber: userInfo.phone,
      }));
    }
  }, [userInfo?.phone]);

  return (
    <>
      <DialogContent>
        <Box
          sx={{
            margin: "auto",
            textAlign: "center",
            maxWidth: 400,
            p: 3,
          }}
        >
          <Typography
            variant="h2"
            fontSize={36}
            fontWeight={700}
            textAlign="center"
            sx={{ textTransform: "capitalize" }}
          >
            Recharge Airtime
          </Typography>

          <Typography>Enter your phone number and amount</Typography>

          <Box pt={5}>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {inputList?.map((item, i) => (
                  <Fragment key={i}>
                    <Grid item xs={12}>
                      <InputText
                        type={item?.type}
                        label={item?.label}
                        name={item?.name}
                        value={
                          state?.[item?.name as keyof RechargeAirtimeModel]
                        }
                        error={
                          !!errors?.[
                            item?.name as keyof RechargeAirtimeErrorModel
                          ]
                        }
                        errorText={
                          errors?.[
                            item?.name as keyof RechargeAirtimeErrorModel
                          ]
                        }
                        onChange={handleChange}
                        onFocus={() => handleFocus(item?.name)}
                      />
                    </Grid>
                  </Fragment>
                ))}

                <Grid item xs={12}>
                  <Box
                    pt={5}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={2}
                  >
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={handleClose}
                      sx={{
                        padding: "8px 20px",
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{
                        padding: "8px 20px",
                      }}
                    >
                      Continue
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* PAYMENT METHOD */}
      <Dialog open={openPaymentOption} maxWidth="md" fullWidth>
        <PaymentCheckout
          handleMakePaymentRequest={handleRechargeRequest}
          togglePaymentCheckout={handleTogglePaymentOption}
          paymentOptions={paymentOptionsList}
          showOnlyPaymentMethod
          embeddedPaymentProcess={airtimeEmbeddedPaymentProcess}

          // checkoutTitle={
          //   state?.phoneNumber ? `Recharge ${state?.phoneNumber}` : ""
          // }
          // checkoutBill={openPaymentOption ? checkoutBill : []}
          // handleMakePaymentRequest={handleRechargeRequest}
          // togglePaymentCheckout={handleTogglePaymentOption}
          // paymentOptions={paymentOptionsList}
          // isCouponFormEnabled
          // couponState={couponState}
          // setCouponState={setCouponState}
          // couponErrors={couponErrors}
          // setCouponErrors={setCouponErrors}
          // handleApplyCoupon={handleApplyCoupon}
          // handleRemoveCoupon={handleRemoveCoupon}
        />
      </Dialog>

      {/* WALLET PAYMENT METHOD */}
      <Dialog open={openWalletPaymentMethod} fullWidth maxWidth="sm">
        <WalletPaymentMethod
          handleClose={handleToggleWalletPaymentMethod}
          walletPaymentState={walletPaymentState}
          handleContinue={handleRechargeRequestUpdate}
        />
      </Dialog>
    </>
  );
};

export default AirtimeRecharge;
